import { ContentType } from 'library/ts/model/enums/content/ContentType';
import { PlayerAdapterEvent } from 'library/ts/player/adapter/events/PlayerAdapterEvent';
import { IPlayerAdapterEventVO } from 'library/ts/player/adapter/interfaces/events/IPlayerAdapterEventVO';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { IVideoAPIInterrupt } from 'tag/ts/tag/interfaces/api/IVideoAPIInterrupt';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IVideoApiInterruptArgument } from 'tag/ts/tag/interfaces/arguments/IVideoApiInterruptArgument';
import { VideoInterruptType } from 'tag/ts/tag/model/enums/VideoInterruptType';

/**
 * @copyright © Paramount Streaming
 * @author  Todd Alexander
 * @description VTG player processor. Tracks player events + applies custom logic as needed.
 */
export class VideoAPIInterrupt extends AbstractTagClass implements IVideoAPIInterrupt {

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'VideoAPIInterrupt'
			}
		);

		// setup tag events handler
		this.tagEventsHandler(
			this.#onTagEvent,
			[
				TagEvent.ON_API_LOAD_VIDEO_REQUEST,
				TagEvent.ON_PLAYER_EVENT,
				TagEvent.ON_TAG_PLAYER_STOP_SUCCESS
			]
		);
	}

	/**
	 * @description flag indicating whether or not to execute interrupt after an ad break
	 * @type {boolean}
	 */
	#executeInterruptAfterAd: boolean = false;
	/**
	 * @description placeholder for callback method to be executed after player is interrupted
	 * @type {VoidFunction}
	 */
	#interruptCallback: VoidFunction;

	/**
	 * @description Stores status of interrupt request. true when player is requested to stop. Means we did interrupt the playback.
	 * @type {boolean}
	 * @private
	 */
	#interrupted: boolean = false;

	/**
	 * @description Returns a flag indicating player interruption
	 * @returns {boolean}
	 */
	get interrupted(): boolean {
		return this.#interrupted;
	}

	/**
	 * @description Stops video playback and executes callback passed to SmartTag
	 */
	#stopPlayer = () => {
		// indicate we have requested player stop so any interested class can check it later
		this.#interrupted = true;

		this.utils.Delay.delay().then(
			() => {
				this.tag.dispatchTagEvent(
					// request player stop
					this.tag.getTagEventVO(
						TagEvent.ON_TAG_PLAYER_STOP_REQUEST,
						this.tag,
						undefined,
						this,
						this.name
					)
				);
			}
		);
	};

	/**
	 * @description Player event handler
	 * @param {IPlayerAdapterEventVO} event
	 */
	#onPlayerEvent = ( event: IPlayerAdapterEventVO ): void => {
		switch ( event.type ) {
			case PlayerAdapterEvent.CONTENT_SEGMENT_START:
			case PlayerAdapterEvent.CONTENT_START:
				if ( this.#executeInterruptAfterAd === true ) {
					this.#stopPlayer();
				}
				break;
		}
	};

	/**
	 * @description Tag application event handler
	 * @param {TagEventVO} event
	 */
	#onTagEvent = ( event: TagEventVO ): void => {
		// filter by tag event type
		switch ( event.type ) {
			case TagEvent.ON_API_LOAD_VIDEO_REQUEST:
				// reset our interrupt flag
				this.#interrupted = false;
				break;
			case TagEvent.ON_PLAYER_EVENT:
				this.#onPlayerEvent( event.data.playerAdapterEvent );
				break;
			case TagEvent.ON_TAG_PLAYER_STOP_SUCCESS:
				if ( this.utils.Type.isFunction( this.#interruptCallback ) ) {
					this.#interruptCallback();
				}
				break;
		}
	};

	/**
	 * @description Handler for Interrupt API, executes player interruption based on given reason
	 * @param {IVideoApiInterruptArgument} argument
	 */
	interrupt = ( argument: IVideoApiInterruptArgument ): void => {
		this.#interruptCallback = argument?.callback;

		switch ( argument?.reason ) {
			case VideoInterruptType.STOP_CONTENT_AFTER_AD:
				// If content type is AD
				if ( this?.tag?.controller?.playerManager?.contentType === ContentType.AD ) {
					// set boolean to true and wait for ads to finish before interrupting player
					this.#executeInterruptAfterAd = true;
				} else {
					this.#stopPlayer();
				}
				break;
			case VideoInterruptType.STOP_CONTENT_MULTI_STREAM:
				// interrupts player when multi stream plugin is opened
				this.#stopPlayer();
				break;
			default:
				break;
		}
	};
}